import { useState } from 'react'
import { DollarSign } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { formatCurrency } from '@/lib/utils'

interface ContributionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  goalName: string
  remaining: number
  onSubmit: (amount: number, note: string) => void
}

export function ContributionDialog({
  open,
  onOpenChange,
  goalName,
  remaining,
  onSubmit,
}: ContributionDialogProps) {
  const [amount, setAmount] = useState('')
  const [note, setNote] = useState('')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const value = parseFloat(amount)
    if (value > 0) {
      onSubmit(value, note)
      setAmount('')
      setNote('')
    }
  }

  const quickAmounts = [50, 100, 250, 500]

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Add Contribution</DialogTitle>
          <DialogDescription>
            Add funds to "{goalName}". {remaining > 0 && `${formatCurrency(remaining)} remaining.`}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Quick Amount Buttons */}
          <div className="flex gap-2">
            {quickAmounts.map((qa) => (
              <Button
                key={qa}
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setAmount(qa.toString())}
                className="flex-1"
              >
                ${qa}
              </Button>
            ))}
          </div>
          
          {/* Amount Input */}
          <div className="space-y-2">
            <Label htmlFor="amount">Amount</Label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="amount"
                type="number"
                min="0.01"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="pl-9"
                placeholder="0.00"
                required
                autoFocus
              />
            </div>
            {remaining > 0 && (
              <Button
                type="button"
                variant="link"
                size="sm"
                className="px-0 h-auto text-xs"
                onClick={() => setAmount(remaining.toFixed(2))}
              >
                Use remaining amount ({formatCurrency(remaining)})
              </Button>
            )}
          </div>
          
          {/* Note */}
          <div className="space-y-2">
            <Label htmlFor="note">Note (Optional)</Label>
            <Textarea
              id="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="What's this contribution for?"
              rows={2}
            />
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!amount || parseFloat(amount) <= 0}>
              Add {amount ? formatCurrency(parseFloat(amount)) : 'Contribution'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
